import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import AppButton from 'components/AppButton/AppButton';
import { TargetGroupCsvDataRow } from 'typings/user';
import { CSVLink } from 'react-csv';
import { DownloadIcon, toaster } from 'evergreen-ui';
import {
    useLazyGetParticipantsActivityQuery,
    useLazyGetParticipantsQuery,
} from '../../../store/api/participants';
import { ParticipantWithActivity } from '../../../typings/participant_activity';
import { formatScores, formatSession } from './utils';
import LinearProgress from '@material-ui/core/LinearProgress';
import { retryWithBackoff } from '../../../utils/retry';

export interface TargetGroupScoreDownloadProps {
    companyId: string;
    groupId: string;
    assessmentName: string;
    targetGroupName: string;
    participantsCount: number;
}

const TargetGroupScoreDownload: FunctionComponent<
    TargetGroupScoreDownloadProps
> = (props) => {
    const {
        companyId,
        groupId,
        assessmentName,
        targetGroupName,
        participantsCount,
    } = props;

    const csvLink = useRef<
        CSVLink & HTMLAnchorElement & { link: HTMLAnchorElement }
    >(null);
    const isRunningRef = useRef<boolean>(false);
    const [csvData, setCsvData] = useState<TargetGroupCsvDataRow[]>([]);

    const [getParticipants] = useLazyGetParticipantsQuery();
    const [getParticipantsActivity] = useLazyGetParticipantsActivityQuery();

    const [isFetching, setIsFetching] = useState(false);
    const [isPaused, setIsPaused] = useState(false);
    const [isDone, setIsDone] = useState(false);
    const [processedCount, setProcessedCount] = useState<number>(0);
    const [resumeToken, setResumeToken] = useState<string | null>(null);
    const [resumeFinalData, setResumeFinalData] = useState<
        Record<string, ParticipantWithActivity>
    >({});

    function wait(ms: number) {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }

    const runDownload = async (
        startToken: string,
        startFinalData: Record<string, ParticipantWithActivity>,
        isResume: boolean,
    ) => {
        if (isRunningRef.current) return;
        isRunningRef.current = true;

        try {
            const limit = 80;
            const finalData: Record<string, ParticipantWithActivity> = {
                ...startFinalData,
            };

            setIsFetching(true);
            setIsPaused(false);
            setIsDone(false);
            setCsvData([]);
            if (!isResume) setProcessedCount(0);

            let nextToken = startToken;
            let lastAttemptedToken = startToken;

            try {
                // eslint-disable-next-line no-constant-condition
                while (true) {
                    const tokenForThisPage = nextToken;
                    lastAttemptedToken = tokenForThisPage;
                    const { participants, participantsActivity, nextNextToken } =
                        await retryWithBackoff(async () => {
                            const participantsPage = await getParticipants({
                                companyIdentifier: companyId,
                                groupId,
                                limit,
                                includeResults: false,
                                includeTotal: false,
                                nextToken: tokenForThisPage,
                            }).unwrap();

                            const activityForPage = await getParticipantsActivity({
                                companyIdentifier: companyId,
                                groupId,
                                userIds: participantsPage.participants.map(
                                    (p) => p.user_id,
                                ),
                            }).unwrap();

                            return {
                                participants: participantsPage.participants,
                                participantsActivity: activityForPage,
                                nextNextToken: participantsPage.nextToken,
                            };
                        });

                    for (const participant of participants) {
                        const participantActivity = participantsActivity.find(
                            (a) => a.invitation.id === participant.invitation_id,
                        );

                        if (participantActivity) {
                            finalData[participant.user_id] = {
                                ...participant,
                                ...participantActivity,
                            };
                        }
                    }

                    setProcessedCount((prev) => prev + participants.length);

                    const next = nextNextToken ?? '';
                    if (!next) break;
                    nextToken = next;
                    await wait(1000);
                }
            } catch (e) {
                console.error('Error getting score data', e);
                setResumeToken(lastAttemptedToken);
                setResumeFinalData(finalData);
                setIsFetching(false);
                setIsPaused(true);
                toaster.warning(
                    'Download paused. Click "Continue download" to resume.',
                );
                return;
            }

            setResumeToken(null);
            setResumeFinalData({});

            if (Object.keys(finalData).length) {
                const scoresArray: TargetGroupCsvDataRow[] = [];

                for (const userId in finalData) {
                    const { scores, sessions } = finalData[userId];
                    if (scores || sessions) {
                        const scoreDataCurrentParticipant: TargetGroupCsvDataRow = {
                            Scan: assessmentName,
                            'Target Group': targetGroupName,
                            user_id: finalData[userId].user_id,
                            Email: finalData[userId].email,
                            'Candidate name':
                                finalData[userId].first_name +
                                ' ' +
                                finalData[userId].last_name,
                        };

                        if (scores) {
                            formatScores(scores, scoreDataCurrentParticipant);
                        }

                        if (sessions) {
                            formatSession(sessions, scoreDataCurrentParticipant);
                        }

                        scoresArray.push(scoreDataCurrentParticipant);
                    }
                }

                const filteredCsvData = scoresArray.filter(
                    (scoreDataOfCurrentParticipant) => {
                        return (
                            !!scoreDataOfCurrentParticipant &&
                            (!!scoreDataOfCurrentParticipant['Date completed'] ||
                                !!scoreDataOfCurrentParticipant.Engagement ||
                                !!scoreDataOfCurrentParticipant['Culture Match'] ||
                                !!scoreDataOfCurrentParticipant.Adhocracy ||
                                !!scoreDataOfCurrentParticipant.Hierarchy ||
                                !!scoreDataOfCurrentParticipant.Market ||
                                !!scoreDataOfCurrentParticipant.Family ||
                                !!scoreDataOfCurrentParticipant.Confidence ||
                                !!scoreDataOfCurrentParticipant.Motivation ||
                                !!scoreDataOfCurrentParticipant[
                                    'Stress resilience'
                                ] ||
                                !!scoreDataOfCurrentParticipant[
                                    'Ease under pressure'
                                ] ||
                                !!scoreDataOfCurrentParticipant[
                                    'General knowledge'
                                ] ||
                                !!scoreDataOfCurrentParticipant[
                                    'Verbal reasoning'
                                ] ||
                                !!scoreDataOfCurrentParticipant[
                                    'Numerical reasoning'
                                ] ||
                                !!scoreDataOfCurrentParticipant[
                                    'Logical reasoning'
                                ])
                        );
                    },
                );

                setCsvData(filteredCsvData);
                setIsDone(true);
            } else {
                setIsFetching(false);
                setProcessedCount(0);
                toaster.notify('No score data available for this group yet.');
            }
        } finally {
            isRunningRef.current = false;
        }
    };

    const downloadScores = () => {
        void runDownload('', {}, false);
    };
    const continueDownload = () => {
        if (resumeToken !== null) {
            void runDownload(resumeToken, resumeFinalData, true);
        }
    };

    useEffect(() => {
        if (csvData.length && isFetching) {
            csvLink?.current?.link.click();
            setIsFetching(false);
            setProcessedCount(0);
        }
    }, [csvData, isFetching]);

    const progress = participantsCount
        ? Math.min((processedCount / participantsCount) * 100, 100)
        : 0;

    return (
        <div aria-label="download score button">
            {!isDone && isFetching && !isPaused && (
                <div>
                    <LinearProgress variant="determinate" value={progress} />
                    <div style={{ fontSize: 12, marginTop: 4 }}>
                        Downloading scores…
                    </div>
                </div>
            )}
            {isPaused && (
                <div>
                    <div style={{ fontSize: 12, marginBottom: 8 }}>
                        Download paused at {processedCount}
                        {participantsCount
                            ? ` / ${participantsCount}`
                            : ''}{' '}
                        candidates.
                    </div>
                    <AppButton
                        justifyContent={'flex-start'}
                        width={'auto'}
                        icon={DownloadIcon}
                        isDisabled={false}
                        onClick={continueDownload}>
                        Continue download
                    </AppButton>
                </div>
            )}
            {!isPaused && (isDone || !isFetching) && (
                <AppButton
                    justifyContent={'flex-start'}
                    width={'auto'}
                    icon={DownloadIcon}
                    isDisabled={isFetching}
                    onClick={downloadScores}>
                    Download all scores
                </AppButton>
            )}
            <CSVLink
                data={csvData}
                filename={targetGroupName + '.csv'}
                className="hidden"
                ref={csvLink}
                target={'_blank'}
            />
        </div>
    );
};

export default TargetGroupScoreDownload;
